'use client';

import Link from 'next/link';
import { usePathname } from 'next/navigation';

import { cn } from '@/lib/utils';
import { MessageSquare, FolderOpen, Megaphone, Users, Mail, PanelLeftClose, PanelLeftOpen, Settings, Share2, ChevronDown, Factory, Plane, Flower2, Target, MoonStar, Bot, LayoutGrid, Zap, BarChart3 } from 'lucide-react';
import { useEffect, useState } from 'react';
import { useActiveProject } from '@/lib/useActiveProject';
import { BUSINESS_PROJECTS, isPlaceholderCollab } from '@/lib/business-projects';
import { useSidebarCollapsed, useMobileNav } from './LayoutShell';

// IA v2 (docs/BEAST-IA-WORKFLOW.md): organized by business object, not by tool.
// Groups expand in place; every old URL keeps working.
type NavChild = { name: string; href: string; icon?: any; tabs?: { name: string; href: string }[] };
type NavItem = { name: string; href: string; icon: any; children?: NavChild[] };
const PROJECT_ICONS: Record<string, any> = { Factory, Plane, Flower2, Target, MoonStar, Bot, Users };

// Per-project tab labels (mirrors /p/[slug]/layout.tsx TAB_DEFS)
const TAB_LABELS: Record<string, string> = {
  overview: 'Overview', performance: 'Performance', websites: 'Websites', improve: 'Improve',
  factory: 'Factory', funnel: 'Funnel', 'video-lab': 'Video Lab', 'ab-tests': 'A/B Tests',
  ads: 'Ads', content: 'Content', crm: 'CRM', email: 'Email', investors: 'Investors',
};
const projectTabs = (p: (typeof BUSINESS_PROJECTS)[number]) => [
  // projects with a defaultTab have no separate Overview — /p/<slug> just
  // redirects there, so showing an 'Overview' link is a lie (Boss, 2026-07-07)
  ...(p.defaultTab ? [] : [{ name: 'Overview', href: `/p/${p.slug}` }]),
  ...p.tabs.map(t => ({
    name: TAB_LABELS[t] || t,
    href: `/p/${p.slug}/${t}${p.funnelId && ['funnel', 'video-lab'].includes(t) ? `?funnel=${p.funnelId}` : ''}`,
  })),
];
const navigation: NavItem[] = [
  {
    name: 'Beast', href: '/brain', icon: Share2, children: [
      { name: 'Brain', href: '/brain' },
      { name: 'Network', href: '/network' },
      { name: 'Portfolio', href: '/portfolio' }, // global gallery of ALL websites — lives under Beast, not inside a project
    ],
  },
  {
    // IA v3 (Boss, 2026-07-07): the Command Center controls PROJECTS.
    // Each business project expands into its own suite at /p/[slug];
    // Websites and Funnels tools live INSIDE projects, not top-level.
    // Boss 2026-07-07: each project is itself expandable into its full tab
    // suite (Overview → Email) — collapsed by default, no wasted space.
    name: 'Projects', href: '/p/wtf', icon: FolderOpen, children: [
      // own projects first (incl. graduated collabs like Ivan) …
      ...BUSINESS_PROJECTS.filter(p => !isPlaceholderCollab(p.slug))
        .map(p => ({ name: p.name, href: `/p/${p.slug}`, icon: PROJECT_ICONS[p.icon] || FolderOpen, tabs: projectTabs(p) })),
      // … then ONE Collabs group with the not-yet-started people under it (Brian, Homiak, Timur)
      {
        name: 'Collabs', href: '/p/collab-brian', icon: Users,
        tabs: BUSINESS_PROJECTS.filter(p => isPlaceholderCollab(p.slug))
          .map(p => ({ name: p.name, href: `/p/${p.slug}` })),
      },
      { name: 'All projects', href: '/projects', icon: LayoutGrid },
    ],
  },
  // The Factory page has existed since the initial build (564 ln, live, returns 200)
  // but was never linked. Boss 2026-08-22: "we removed it from this command center,
  // but I had it in factory" — the page survived, only the nav entry was dropped.
  { name: 'Pipeline', href: '/factory', icon: Factory },
  // Insights — live data-analysis engine over seo/funnels/ads/improve DBs (Data Master Plan).
  { name: 'Insights', href: '/insights', icon: BarChart3 },
  { name: 'Content', href: '/content', icon: Megaphone },
  { name: 'CRM', href: '/crm', icon: Users },
  { name: 'Email', href: '/email', icon: Mail },
  { name: 'Chat', href: '/sessions', icon: MessageSquare },
  { name: 'Settings', href: '/settings', icon: Settings },
  // UI switch — flips to the Control Room (ui-future). Boss 2026-07-08:
  // "It switches from one UI to another." Counterpart link lives in future Chrome.
  { name: 'Control Room', href: '/future', icon: Zap },
];

const groupRoutes = (item: NavItem) => (item.children ? item.children.map(c => c.href) : [item.href]);

export function Sidebar() {
  const pathname = usePathname();
  const [activeProject] = useActiveProject();
  const [collapsed, setCollapsed] = useSidebarCollapsed();
  const { open: mobileOpen, setOpen: setMobileOpen } = useMobileNav();
  const [groupPrefs, setGroupPrefs] = useState<Record<string, boolean>>({});
  const [projPrefs, setProjPrefs] = useState<Record<string, boolean>>({});
  // Improve inbox badge — count of proposed recommendations awaiting a decision.
  const [improveCount, setImproveCount] = useState(0);
  useEffect(() => {
    fetch('/api/improve/overview')
      .then(r => r.json())
      .then(d => setImproveCount(d?.byStatus?.proposed || 0))
      .catch(() => {});
  }, [pathname]);

  return (
    <aside className={cn(
      'glass-chrome fixed left-3 top-3 bottom-3 z-50 flex flex-col transition-[width,transform] duration-200 overflow-visible',
      collapsed ? 'w-16' : 'w-60',
      mobileOpen ? 'translate-x-0' : '-translate-x-[110%] lg:translate-x-0'
    )}>
      {/* Logotype — brushed metal */}
      <div className="flex-shrink-0 relative border-b border-white/[0.06]">
        {collapsed ? (
          <div className="flex items-center justify-center h-16">
            <span className="metal-text text-lg font-black">B</span>
          </div>
        ) : (
          <div className="px-5 py-5">
            <p className="metal-text text-[26px] font-black leading-none select-none">BEAST</p>
            <p className="text-[9px] tracking-[0.35em] text-gray-500 mt-1.5 select-none">COMMAND CENTER</p>
            {/* ⇄ HOLO MODE — the orb / Beast Mode voice engine lives ONLY in the holo Control
                Room now (Boss 2026-07-12 'I don't need Beast Mode in classic UI, only in holo').
                Classic UI keeps this link to jump into holo; it no longer mounts a voice engine,
                so the two UIs can never both listen at once (kills the double-speak). */}
            <a href="/future"
              className="mt-2 inline-block text-[9px] uppercase tracking-[0.3em] text-cyan-700 hover:text-cyan-300 transition-colors select-none">
              ⇄ holo mode
            </a>
          </div>
        )}
        <button
          onClick={() => setCollapsed(!collapsed)}
          className="absolute -right-3 top-1/2 -translate-y-1/2 w-6 h-6 rounded-full bg-[#141a22] border border-white/10 flex items-center justify-center text-gray-500 hover:text-cyan-400 hover:border-cyan-500/40 transition-colors z-50"
        >
          {collapsed ? <PanelLeftOpen className="w-3 h-3" /> : <PanelLeftClose className="w-3 h-3" />}
        </button>
      </div>

      {/* Navigation */}
      <nav className={cn('flex flex-col gap-1 pt-4 overflow-y-auto', collapsed ? 'px-2' : 'px-4')}>
        {navigation.map((item) => {
          const groupActive = groupRoutes(item).some(r => pathname.startsWith(r));
          // User toggle wins; otherwise auto-expand the group holding the active route.
          const expanded = !!item.children && (groupPrefs[item.name] ?? groupActive);

          const href = item.href === '/projects' && activeProject
            ? `/projects/${activeProject.slug}`
            : item.href;

          return (
            <div key={item.name}>
              <Link
                href={href}
                onClick={(e) => {
                  if (item.children && !collapsed) {
                    // Group headers are pure toggles: click expands, click again collapses.
                    e.preventDefault();
                    setGroupPrefs(p => ({ ...p, [item.name]: !expanded }));
                    return;
                  }
                  setMobileOpen(false);
                }}
                title={collapsed ? item.name : undefined}
                className={cn(
                  'flex items-center rounded-xl transition-all',
                  collapsed ? 'justify-center px-0 py-2.5' : 'gap-3 px-3 py-2.5 text-sm',
                  groupActive
                    ? 'bg-gradient-to-r from-cyan-500/15 to-blue-500/10 text-cyan-300 shadow-[inset_0_1px_0_rgba(255,255,255,0.08),0_0_16px_rgba(0,240,255,0.08)] border border-cyan-500/20'
                    : 'border border-transparent text-gray-400 hover:bg-white/5 hover:text-white'
                )}
              >
                <item.icon className="h-4 w-4 flex-shrink-0" />
                {!collapsed && <span>{item.name}</span>}
                {!collapsed && item.name === 'Websites' && improveCount > 0 && (
                  <span className="ml-auto text-[10px] font-semibold px-1.5 py-0.5 rounded-full bg-cyan-500/15 text-cyan-300">{improveCount}</span>
                )}
                {!collapsed && item.children && (
                  <ChevronDown className={cn('h-3 w-3 transition-transform', item.name === 'Websites' && improveCount > 0 ? '' : 'ml-auto', expanded && 'rotate-180')} />
                )}
              </Link>
              {!collapsed && expanded && item.children && (
                <div className="ml-7 mt-0.5 mb-1 flex flex-col gap-0.5 border-l border-white/[0.06] pl-3">
                  {item.children.map(c => {
                    // longest-prefix match so "Overview" (/funnels) doesn't stay lit on /funnels/video-lab
                    const matches = item.children!.filter(o => pathname === o.href || pathname.startsWith(o.href + '/'));
                    const best = matches.sort((a, b) => b.href.length - a.href.length)[0];
                    const childActive = best?.href === c.href;
                    const projExpanded = !!c.tabs && (projPrefs[c.href] ?? childActive);
                    return (
                    <div key={c.href}>
                      <Link
                        href={c.href}
                        onClick={(e) => {
                          if (c.tabs) {
                            // project rows toggle their tab list in place
                            e.preventDefault();
                            setProjPrefs(p => ({ ...p, [c.href]: !projExpanded }));
                            return;
                          }
                          setMobileOpen(false);
                        }}
                        className={cn(
                          'rounded-lg px-2 py-1.5 text-[13px] transition-colors flex items-center w-full',
                          childActive
                            ? 'text-cyan-300 bg-cyan-500/10'
                            : 'text-gray-500 hover:text-white hover:bg-white/5'
                        )}
                      >
                        {c.icon && <c.icon className="h-3.5 w-3.5 mr-2 flex-shrink-0" />}
                        {c.name}
                        {c.href === '/improve' && improveCount > 0 && (
                          <span className="ml-auto text-[10px] font-semibold px-1.5 py-0.5 rounded-full bg-cyan-500/15 text-cyan-300">{improveCount}</span>
                        )}
                        {c.tabs && <ChevronDown className={cn('h-3 w-3 ml-auto transition-transform', projExpanded && 'rotate-180')} />}
                      </Link>
                      {projExpanded && c.tabs && (
                        <div className="ml-3.5 mt-0.5 mb-1 flex flex-col gap-0.5 border-l border-white/[0.06] pl-2.5">
                          {c.tabs.map(t => {
                            const tPath = t.href.split('?')[0];
                            const tMatches = c.tabs!.filter(o => { const p2 = o.href.split('?')[0]; return pathname === p2 || pathname.startsWith(p2 + '/'); });
                            const tBest = tMatches.sort((a, b) => b.href.length - a.href.length)[0];
                            const tActive = tBest && tBest.href.split('?')[0] === tPath && (pathname === tPath || pathname.startsWith(tPath + '/'));
                            return (
                              <Link key={t.href} href={t.href} onClick={() => setMobileOpen(false)}
                                className={cn('rounded-md px-2 py-1 text-xs transition-colors',
                                  tActive ? 'text-cyan-300 bg-cyan-500/10' : 'text-gray-600 hover:text-white hover:bg-white/5')}>
                                {t.name}
                              </Link>
                            );
                          })}
                        </div>
                      )}
                    </div>
                    );
                  })}
                </div>
              )}
            </div>
          );
        })}
      </nav>

      {/* Spacer */}
      <div className="flex-1" />

      {/* Minimal bottom padding */}
      <div className="h-4" />
    </aside>
  );
}
